'use client';

import Link from 'next/link';
import { type ComponentProps, useState } from 'react';

type LocalizedClientLinkProps = ComponentProps<typeof Link>;

/**
 * Thin `next/link` wrapper that, unless the caller passes an explicit
 * `prefetch` value, gates prefetching on hover/touch intent instead of
 * Next's default viewport-based auto-prefetch. Viewport prefetching fires
 * for every link merely scrolled into view — on a page with many links
 * (product grids, category menus) that multiplies into a lot of avoidable
 * server-side render work. Hover/touch is a real intent signal, so it keeps
 * the UX benefit without the fan-out cost.
 */
const LocalizedClientLink = ({
  children,
  href,
  prefetch,
  onMouseEnter,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  onTouchCancel,
  ...props
}: LocalizedClientLinkProps) => {
  const [active, setActive] = useState(false);

  let resolvedPrefetch: LocalizedClientLinkProps['prefetch'] = active
    ? null
    : false;
  if (prefetch !== undefined) {
    resolvedPrefetch = prefetch;
  }

  return (
    <Link
      href={href}
      prefetch={resolvedPrefetch}
      onMouseEnter={(event) => {
        setActive(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setActive(false);
        onMouseLeave?.(event);
      }}
      onTouchStart={(event) => {
        setActive(true);
        onTouchStart?.(event);
      }}
      onTouchEnd={(event) => {
        setActive(false);
        onTouchEnd?.(event);
      }}
      onTouchCancel={(event) => {
        setActive(false);
        onTouchCancel?.(event);
      }}
      {...props}
    >
      {children}
    </Link>
  );
};

export default LocalizedClientLink;
